import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gift, Copy, Check, X } from "lucide-react";
import api from "@/lib/api";

export const LeadCapturePopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Note: Only stores a boolean "true" flag (not sensitive data) to prevent popup re-showing
    const alreadySubscribed = document.cookie.includes("keyz-lead-subscribed=true");
    if (alreadySubscribed) return;
    const timer = setTimeout(() => setOpen(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/leads", { email });
      setCode(res.data.discount_code);
      setSubmitted(true);
      document.cookie = "keyz-lead-subscribed=true; path=/; max-age=31536000; SameSite=Lax";
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Only persist dismissal if they submitted email
    if (submitted) document.cookie = "keyz-lead-subscribed=true; path=/; max-age=31536000; SameSite=Lax";
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm" data-testid="lead-capture-popup">
        {!submitted ? (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-sandy/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-sandy" />
                </div>
              </div>
              <DialogTitle className="font-cormorant text-2xl font-light text-midnight text-center">
                First-Time Explorer?
              </DialogTitle>
              <DialogDescription className="font-manrope text-sm text-midnight/50 text-center">
                Get an exclusive 10% discount on your first adventure in Saint Lucia.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <Input
                data-testid="lead-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="font-manrope text-sm text-center"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
              <Button
                data-testid="lead-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full rounded-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm h-10"
              >
                {submitting ? "Getting your code..." : "Get My 10% Discount"}
              </Button>
              <p className="font-manrope text-[10px] text-midnight/30 text-center">
                No spam. Just adventure inspiration and exclusive deals.
              </p>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-canopy/10 flex items-center justify-center">
                  <Check className="w-6 h-6 text-canopy" />
                </div>
              </div>
              <DialogTitle className="font-cormorant text-2xl font-light text-midnight text-center">
                Your Discount Code
              </DialogTitle>
              <DialogDescription className="font-manrope text-sm text-midnight/50 text-center">
                Use this code at checkout for 10% off your first booking.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <div
                className="flex items-center justify-between bg-bone border border-sandy/20 p-4 cursor-pointer hover:border-sandy transition-colors duration-200"
                onClick={copyCode}
                data-testid="discount-code-display"
              >
                <span className="font-manrope text-lg font-semibold tracking-wider text-midnight">{code}</span>
                {copied ? <Check className="w-4 h-4 text-canopy" /> : <Copy className="w-4 h-4 text-midnight/30" />}
              </div>
              <p className="font-manrope text-[10px] text-midnight/30 text-center mt-2">Click to copy</p>
              <Button
                data-testid="lead-close-btn"
                onClick={handleClose}
                variant="outline"
                className="w-full mt-4 rounded-full font-manrope text-sm"
              >
                Start Exploring
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
